/**
 * Generate Blueprint Tool (Orchestrator-level)
 *
 * Generates a visual blueprint layout diagram before calling
 * writeSlide for thorough layout edits.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    GenerateBlueprintParamsSchema,
    type GenerateBlueprintParams,
    type GenerateBlueprintResult,
} from "./generateBlueprint.schema";
import { BlueprintService } from "../../../../services/blueprintService";

export class GenerateBlueprintTool extends ToolBase<GenerateBlueprintParams, GenerateBlueprintResult> {
    public readonly name = "generateBlueprint";
    public readonly description: ToolDescription;
    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
    ];

    public readonly systemPromptDescription = `### generateBlueprint
Generate a visual blueprint showing the expected result of a layout change.
Gemini looks at the current slide image and your intent, then produces a layout diagram showing the target positions and sizes.
The blueprint image auto-injects into your next message so you can see the target layout before calling writeSlide.

Use this tool BEFORE calling writeSlide for THOROUGH (layout-changing) edits.

**When to use:**
- Adding new shapes/content
- Removing shapes/content
- Resizing or repositioning shapes
- Any layout-changing edit

**Fields:**
- \`intent\` (required): Describe the layout change. Be specific about what is being added/removed/moved.
- \`constraints\` (optional but recommended): List sections/elements that must NOT change. If you don't mention a section, Gemini may move it.
- \`slideImageId\` (optional): Pass the image ID from readSlide to skip re-capturing the slide.

**Pattern example:**
\`\`\`json
{
  "slideIndex": 5,
  "slideImageId": "slide_318_img_1739...",
  "intent": "Add a third item to the left section. The left section should widen to accommodate 3 items while the right section narrows.",
  "constraints": "The header and footer must NOT change. The top row of sections must NOT change."
}
\`\`\``;

    private blueprintService: BlueprintService;

    constructor() {
        super();
        this.blueprintService = new BlueprintService();
        this.description = new ToolDescription(
            "generateBlueprint",
            "Generate a visual blueprint layout diagram for a target slide before calling writeSlide.",
            GenerateBlueprintParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<GenerateBlueprintResult> {
        const parseResult = safeParseToolParams(GenerateBlueprintParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }

        const params = parseResult.data;
        console.log(`[GenerateBlueprintTool] Generating blueprint for slide ${params.slideIndex}`);

        const result = await this.blueprintService.generateBlueprint(params);
        if (!result.success) {
            return {
                success: false,
                error: result.error || 'Blueprint generation failed',
                message: 'Blueprint generation failed',
            };
        }

        const imageIds = [
            ...(result.originalSlideImageId ? [result.originalSlideImageId] : []),
            ...(result.blueprintId ? [result.blueprintId] : []),
        ];

        return {
            success: true,
            blueprintId: result.blueprintId,
            originalSlideImageId: result.originalSlideImageId,
            message: `Blueprint generated for slide ${params.slideIndex}. The original slide and blueprint images will appear in your next message. Use them as visual reference when constructing writeSlide search/replace blocks.`,
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
        };
    }
}

export default GenerateBlueprintTool;
